<template>
    <dynamic-link class="product-highlight" :class="`product-highlight--${data.type}`" :to="data.target_url">
        <div v-if="!mobileLayout" class="picture-cover">
            <a-picture :link="data.file.url" fit="cover" :alt="alt" />
        </div>
        <div class="product-highlight__content">
            <div class="product-highlight__content-wrap">
                <a-label>
                    {{ data.label_title }}
                </a-label>
                <a-dangerous-html :content="data.label_text" />
            </div>
            <a-picture v-if="mobileLayout" :link="data.file.url" :alt="alt" />
            <!-- without a target the card itself is a <button>: a nested one in server HTML makes the parser close the
                 card early and leave a stray button behind, so it is added on the client only -->
            <a-client-only v-if="!data.target_url">
                <a-button type="action" :text="data.button_text" />
            </a-client-only>
            <a-button v-else type="action" :text="data.button_text" />
        </div>
    </dynamic-link>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AButton from '@core/components/button/button.vue';
import AClientOnly from '@core/components/client-only/client-only.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import ALabel from '@core/components/label/label.vue';
import APicture from '@core/components/picture/picture.vue';
import { useBreakpoint } from '@core/composables/useBreakpoint';
import type { BodyVariant, HighlightProductCardData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockHighlightProductCard',
});

const props = defineProps<{
    id?: string;
    data: HighlightProductCardData;
    variant: BodyVariant;
}>();

const { isMobile } = useBreakpoint();

// Blog never switched to the mobile layout (its breakpoint was undefined); kept for visual parity, see WEB-49996
const mobileLayout = computed(() => props.variant !== 'blog' && isMobile.value);

const alt = computed(() => props.data.label_text || props.data.label_title);
</script>

<style lang="pcss" scoped>
.body-renderer .product-highlight {
    display: flex;
    margin-inline-start: -16px;
    margin-inline-end: -16px;
    margin-bottom: 16px;
    padding-top: 24px;
    padding-bottom: 16px;
    padding-inline-start: 16px;
    padding-inline-end: 16px;
    box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
    background: var(--av-solid-brand-lightest);
    border-top: 1px solid var(--av-brand-light);
    border-bottom: 1px solid var(--av-brand-light);

    @media (--viewport-tablet) {
        display: flex;
        align-items: stretch;
        padding: 0;
        border-radius: 8px;
        border: 2px solid var(--av-brand-light);
        background: var(--av-solid-brand-lightest);
        overflow: hidden;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;

        @media (--viewport-tablet) {
            padding: 24px;
        }

        .a-dangerous-html {
            @mixin lead-accent;
            color: var(--av-nav-primary);
        }

        .a-picture {
            border-radius: 6px;
            overflow: hidden;
            margin-inline: auto;
        }

        .a-button {
            width: 100%;

            @media (--viewport-tablet) {
                width: auto;
            }
        }
    }

    &__content-wrap {
        display: flex;
        gap: 8px;
        flex-direction: column;
        align-items: flex-start;
    }

    .picture-cover {
        @media (--viewport-tablet) {
            position: relative;
            width: 242px;
            min-height: 228px;
            flex-shrink: 0;
        }
    }

    .a-picture {
        @media (--viewport-tablet) {
            position: absolute;
            inset: 0;
            margin: auto;

            :deep(img) {
                width: 100%;
                height: 100%;
            }
        }
    }

    &--super {
        background: linear-gradient(133deg, #00204D 20.01%, #1769D1 99.44%);
        border: 1px solid var(--av-brand-secondary);

        .a-dangerous-html {
            color: var(--av-inversed-primary);
        }
    }
}

.variant-blog .product-highlight {
    flex-flow: column;
    gap: 16px;

    @media (--viewport-tablet) {
        flex-flow: row nowrap;
        gap: unset;
        margin: 48px 0;
    }
}

/* TRU stretched the image on every screen size */
.variant-tru .product-highlight .a-picture :deep(img) {
    width: 100%;
    height: 100%;
}

.variant-resource .product-highlight {
    box-shadow: 0 10px 20px 0 rgba(36, 49, 67, 0.20);
    background: var(--av-solid-brand-lightest, #F4F7FC);
    border-top: 1px solid rgba(38, 104, 197, 0.30);
    border-bottom: 1px solid rgba(38, 104, 197, 0.30);

    @media (--viewport-tablet) {
        margin: 56px 54px;
        border: 2px solid var(--av-brand-light, rgba(38, 104, 197, 0.30));
        background: var(--av-solid-brand-lightest, #F4F7FC);
    }

    &.product-highlight--super {
        background: linear-gradient(133deg, #00204D 20.01%, #1769D1 99.44%);
        border: 1px solid var(--av-brand-secondary, #408BEA);

        .a-dangerous-html {
            color: var(--av-inversed-primary, #FFF);
        }
    }
}
</style>
